import '../../styles/tile-select.css'
import {useState, useMemo} from "react";
import {Popover, Tag} from "antd";
import {PROJECTIONS, getProjectionLabel, loadProjectionText, findProjectionKeyByText} from "../../helpers/ProjectionHelper";
import {GlobalOutlined} from "@ant-design/icons";

/**
 * Projection selector component for the map
 * Allows switching between project projection and available system projections
 */
export default function ProjectionSelect({
    projectProjection,  // WKT text from the project (null if no prj file)
    activeProjection,   // Currently active projection { text, name, isProject }
    onProjectionChange, // Callback when projection changes
    userPreference      // User's default projection key (e.g., 'NC')
}) {
    const [popoverOpen, setPopoverOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    const getCompactLabel = (label) => label
        ?.replace(' State Plane', '')
        ?.replace('Project ', '')

    // Detect the actual projection name from WKT text
    const projectProjectionInfo = useMemo(() => {
        if (!projectProjection) return null
        const detectedKey = findProjectionKeyByText(projectProjection)
        if (detectedKey) {
            return {
                key: detectedKey,
                label: getProjectionLabel(detectedKey),
                displayLabel: `Project (${getProjectionLabel(detectedKey)})`
            }
        }
        return {
            key: null,
            label: 'Unknown',
            displayLabel: 'Project (Custom)'
        }
    }, [projectProjection])

    const handleProjectionSelect = async (key) => {
        if (key === 'PROJECT' && projectProjection) {
            onProjectionChange({
                text: projectProjection,
                name: projectProjectionInfo?.label || 'Project',
                isProject: true
            })
        } else {
            setLoading(true)
            try {
                const projText = await loadProjectionText(key)
                if (projText) {
                    onProjectionChange({
                        text: projText,
                        name: getProjectionLabel(key),
                        isProject: false
                    })
                }
            } finally {
                setLoading(false)
            }
        }
        setPopoverOpen(false)
    }

    const getProjectionOptions = () => {
        const options = []
        
        // Add project projection option if available
        if (projectProjection) {
            options.push({
                key: 'PROJECT',
                label: projectProjectionInfo?.displayLabel || 'Project',
                isActive: activeProjection?.isProject,
                isProject: true
            })
        }

        // Add all system projections
        Object.entries(PROJECTIONS).forEach(([key, proj]) => {
            options.push({
                key,
                label: proj.label,
                isActive: !activeProjection?.isProject && activeProjection?.name === proj.label,
                isDefault: key === userPreference
            })
        })

        return options
    }

    const options = getProjectionOptions()

    const content = (
        <div style={{ width: 168 }}>
            <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 6, borderBottom: '1px solid #eee', paddingBottom: 4 }}>
                Projection
            </div>
            {options.map(opt => (
                <div
                    key={opt.key}
                    onClick={() => handleProjectionSelect(opt.key)}
                    style={{
                        padding: '5px 7px',
                        cursor: 'pointer',
                        backgroundColor: opt.isActive ? '#e6f7ff' : 'transparent',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 2,
                        fontSize: 11,
                        lineHeight: 1.2,
                        transition: 'background-color 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                        if (!opt.isActive) {
                            e.currentTarget.style.backgroundColor = '#f5f5f5'
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!opt.isActive) {
                            e.currentTarget.style.backgroundColor = 'transparent'
                        }
                    }}
                >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {getCompactLabel(opt.label)}
                        </span>
                        {opt.isProject && <Tag style={{ marginInlineEnd: 0, fontSize: 9, lineHeight: '14px', paddingInline: 4 }}>[Project]</Tag>}
                        {opt.isDefault && <Tag color="blue" style={{ marginInlineEnd: 0, fontSize: 9, lineHeight: '14px', paddingInline: 4 }}>Def</Tag>}
                    </span>
                    {opt.isActive && <span style={{ color: '#1890ff', fontSize: 11, flexShrink: 0 }}>✓</span>}
                </div>
            ))}
        </div>
    )

    return (
        <Popover
            content={content}
            trigger="click"
            open={popoverOpen}
            onOpenChange={setPopoverOpen}
            placement="topRight"
        >
            <button
                className="leaflet-layer-select-btn"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '3px 7px',
                    fontSize: 11,
                    minWidth: 0,
                    maxWidth: 110
                }}
                disabled={loading}
            >
                <GlobalOutlined style={{ fontSize: 12 }} />
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {getCompactLabel(activeProjection?.name || 'Unknown')}
                    </span>
                    {activeProjection?.isProject && <Tag style={{ marginInlineEnd: 0, fontSize: 9, lineHeight: '14px', paddingInline: 4 }}>[Project]</Tag>}
                </span>
            </button>
        </Popover>
    )
}
